import React, { useState, useRef } from "react";

import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BASE_URL from "../config/appconfig";
import XLSX from "xlsx-js-style";

const REQUIRED_COLUMNS = ["email"];
const ALL_COLUMNS = ["email"];
const COLUMN_LABELS = {
  email: "Email",
};

const normalizeKey = (key) => key.toLowerCase().trim().replace(/\s+/g, "_");

const UploadContactsDialog = ({
  open,
  handleClose,
  refreshContacts,
  showSnackbar,
}) => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const reset = () => {
    setRows([]);
    setErrors([]);
    setDuplicates([]);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const downloadTemplate = () => {
    const headers = ["Email"];

    const ws = XLSX.utils.aoa_to_sheet([headers]);

    // Style header cell
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: 0 });
    ws[cellRef].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4F81BD" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    ws["!cols"] = [{ wch: 32 }];
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");

    XLSX.writeFile(wb, "contact_list_template.xlsx");
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const parsed = raw.map((r) => {
        const normalized = {};
        Object.keys(r).forEach((k) => {
          normalized[normalizeKey(k)] = String(r[k]).trim();
        });
        return normalized;
      });

      // Validate each row
      const rowErrors = parsed
        .map((r, i) => {
          const missing = REQUIRED_COLUMNS.filter((col) => !r[col]);
          const emailInvalid =
            r.email &&
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(r.email);
          const msgs = [];
          if (missing.length) msgs.push(`Missing: ${missing.join(", ")}`);
          if (emailInvalid) msgs.push("Invalid email");
          return { index: i, row: i + 2, messages: msgs };
        })
        .filter((e) => e.messages.length > 0);

      setErrors(rowErrors);
      setRows(parsed);

      // Check for duplicate emails against existing contacts
      const validEmails = parsed
        .filter((_, i) => !rowErrors.some((e) => e.index === i))
        .map((r) => r.email)
        .filter(Boolean);

      if (validEmails.length > 0) {
        fetch(`${BASE_URL}/api/contact-list/check-emails-bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ emails: validEmails }),
        })
          .then((res) => res.json())
          .then((result) => setDuplicates(result.duplicates || []))
          .catch(() => {});
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validRows = rows.filter((_, i) => {
    const hasError = errors.some((e) => e.index === i);
    const isDuplicate = duplicates.includes(rows[i]?.email?.toLowerCase());
    return !hasError && !isDuplicate;
  });

  const handleSubmit = async () => {
    if (validRows.length === 0) return;
    setLoading(true);
    try {
      const contacts = validRows.map((r) => ({
        email: r.email,
      }));

      const res = await fetch(`${BASE_URL}/api/contact-list/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contacts }),
      });
      const result = await res.json();

      if (result.success) {
        showSnackbar(
          `${result.inserted} contact(s) added successfully`,
          "success"
        );
        refreshContacts();
        reset();
        handleClose();
      } else {
        showSnackbar(result.message || "Upload failed", "error");
      }
    } catch {
      showSnackbar("Server error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: "editor-dialog-paper" }}
    >
      <DialogContent className="editor-dialog-content">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            className="editor-dialog-title"
            sx={{ mb: "0 !important" }}
          >
            Upload Contact List
          </Typography>
          <Button
            size="small"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={downloadTemplate}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#374151",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Download Template
          </Button>
        </Box>

        {/* File picker */}
        <Box
          sx={{
            border: "2px dashed #d1d5db",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: "#fafafa",
            mb: 3,
            "&:hover": { borderColor: "#ef2d2d", bgcolor: "#fff5f5" },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <FileUploadOutlinedIcon
            sx={{ fontSize: 36, color: "#9ca3af", mb: 1 }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
            {fileName || "Click to choose an Excel or CSV file"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>
            Supported formats: .xlsx, .xls, .csv
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 1 }}>
            Required column: <strong>Email</strong>
          </Typography>
        </Box>

        {/* Error summary */}
        {(errors.length > 0 || duplicates.length > 0) && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: "#fff5f5",
              borderRadius: 1,
              border: "1px solid #fecaca",
            }}
          >
            {errors.length > 0 && (
              <>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: "#dc2626", mb: 1 }}
                >
                  {errors.length} row(s) have errors and will be skipped:
                </Typography>
                {errors.map((e) => (
                  <Typography key={e.row} sx={{ fontSize: 11, color: "#dc2626" }}>
                    Row {e.row}: {e.messages.join(" · ")}
                  </Typography>
                ))}
              </>
            )}
            {duplicates.length > 0 && (
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: "#b45309", mt: errors.length > 0 ? 1 : 0 }}
              >
                {duplicates.length} row(s) already exist in contacts and will be skipped.
              </Typography>
            )}
          </Box>
        )}

        {/* Preview table */}
        {rows.length > 0 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}
              >
                Preview
              </Typography>
              <Chip
                label={`${validRows.length} valid`}
                size="small"
                sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontSize: 11 }}
              />
              {errors.length > 0 && (
                <Chip
                  label={`${errors.length} invalid`}
                  size="small"
                  sx={{ bgcolor: "#fee2e2", color: "#dc2626", fontSize: 11 }}
                />
              )}
              {duplicates.length > 0 && (
                <Chip
                  label={`${duplicates.length} duplicate`}
                  size="small"
                  sx={{ bgcolor: "#fef3c7", color: "#b45309", fontSize: 11 }}
                />
              )}
            </Box>
            <TableContainer
              sx={{
                maxHeight: 280,
                border: "1px solid #e5e7eb",
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontSize: 11, fontWeight: 700, bgcolor: "#f9fafb" }}
                    >
                      #
                    </TableCell>
                    {ALL_COLUMNS.map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#f9fafb",
                        }}
                      >
                        {COLUMN_LABELS[col]}
                        {REQUIRED_COLUMNS.includes(col) && (
                          <span style={{ color: "red" }}> *</span>
                        )}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{ fontSize: 11, fontWeight: 700, bgcolor: "#f9fafb" }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    const rowError = errors.find((e) => e.index === i);
                    const isDuplicate = !rowError && duplicates.includes(r.email?.toLowerCase());
                    const rowBg = rowError ? "#fff5f5" : isDuplicate ? "#fffbeb" : "inherit";
                    return (
                      <TableRow key={i} sx={{ bgcolor: rowBg }}>
                        <TableCell sx={{ fontSize: 11, color: "#6b7280" }}>
                          {i + 1}
                        </TableCell>
                        {ALL_COLUMNS.map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              fontSize: 11,
                              maxWidth: 120,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r[col] || (
                              <span style={{ color: "#d1d5db" }}>—</span>
                            )}
                          </TableCell>
                        ))}
                        <TableCell>
                          {rowError ? (
                            <Chip
                              label="Invalid"
                              size="small"
                              sx={{ bgcolor: "#fee2e2", color: "#dc2626", fontSize: 10 }}
                            />
                          ) : isDuplicate ? (
                            <Chip
                              label="Duplicate"
                              size="small"
                              sx={{ bgcolor: "#fef3c7", color: "#b45309", fontSize: 10 }}
                            />
                          ) : (
                            <Chip
                              label="OK"
                              size="small"
                              sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontSize: 10 }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={validRows.length === 0 || loading}
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#ef2d2d",
              "&:hover": { backgroundColor: "#d91c1c" },
            }}
          >
            {loading
              ? "Uploading..."
              : `Upload ${validRows.length > 0 ? `(${validRows.length})` : ""}`}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadContactsDialog;
